import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useReferrals } from '../../../lib/use-referrals';
import { useRewardRules } from '../../../lib/use-reward-rules';
import { earnedCredits } from '../../../lib/social-rewards';
import { Modal } from '../../Modal';
import { Badge, Button, ScrollArea } from '../../symphony/ui';
import { SETTINGS_PAGE } from './settings-shared';
import { EarnCreditsSection } from './EarnCreditsSection';
import { ProfileLinkShareSection } from '../../symphony/profile-links/ProfileLinkShareSection';
import { InviteLinkRow } from './InviteLinkRow';
import { useShareApp } from './use-share-app';
import { ReferralAvatar } from './ReferralAvatar';
import { ShareAppModalMobile } from './ShareAppModalMobile';
import styles from './ShareAppModal.module.css';

// The "Share the app" modal (Figma 12801-107843 desktop, 5339-2840 mobile): an invite explanation +
// the agent-runs reward, the caller's own invite link with Copy, and the "Successful referrals" list
// read from useReferrals.
//
// **The desktop footer carries the invite link and Close.** The share-sheet CTA that used to sit there was doing
// the same job as Copy on this surface and worse: most desktop browsers have no share sheet, so
// `navigator.share` was absent and the button silently fell back to writing the same link to the
// clipboard — two controls, one outcome, and the louder one gave no feedback that anything happened.
// Taking the link is now the single ask, which is why Copy wears the primary skin. The phone layout
// KEEPS the CTA (see ShareAppModalMobile): a real OS share sheet is a capability web only has there.
//
// One shared logic core, two layouts: the desktop DS modal here, and a full-screen phone screen
// (ShareAppModalMobile) when the mobile Settings tree opens it (`variant="mobile"`). Everything below
// — the referral counts, the earned runs, the real share, the BI-wrapped close — is computed once and
// handed to whichever view renders.
export function ShareAppModal({ open, onClose, variant = 'desktop' }: { open: boolean; onClose: () => void; variant?: 'desktop' | 'mobile' }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  // Gated on `open` because the Settings row keeps this mounted whether or not it is showing: an ungated
  // read costs a recipient resolve plus a ListReferralViews RPC on every Settings render, and reports a
  // load in BI for a modal nobody opened.
  const { referrals, shareLink, loading, error } = useReferrals({ enabled: open });
  const share = useShareApp(shareLink);
  useLoadBi('referrals', { isLoading: loading, isError: error });
  // What a referral pays is served, never assumed. When the read fails every line below drops its
  // number rather than printing one that may no longer be what the program pays.
  const { referrerReward, premiumConversionReward } = useRewardRules({ enabled: open });

  const joined = referrals.length;
  const earned = earnedCredits(referrerReward, joined);
  const showReferrals = loading || joined > 0;

  const close = () => {
    logModalActionBi({ ...SETTINGS_PAGE, modal_name: 'share_app', action: 'close' });
    onClose();
  };

  const steps = [
    { title: t('settings.shareApp.step1.title'), body: t('settings.shareApp.step1.body') },
    {
      title: referrerReward
        ? t('settings.shareApp.step2.title', {
            credits: referrerReward.credits,
            agents: referrerReward.subAgentRuns,
            briefs: referrerReward.agentMeetings,
          })
        : t('settings.shareApp.step2.titleNoAmount'),
      body: t('settings.shareApp.step2.body'),
    },
  ];

  const earnedTitle =
    earned === null
      ? t('settings.shareApp.earnedUnknown')
      : earned === 0
        ? t('settings.shareApp.earnedEmpty')
        : t('settings.shareApp.earned', { count: earned });
  // REFERRAL copy, so it belongs to the referral program — not to the social "Earn credits" section it
  // used to close, where it read as one of the social steps. Same key, same gate as before: it names
  // both numbers, so it renders only when BOTH are served (a half-served sentence would print one real
  // amount next to a hole).
  const referralNote =
    referrerReward && premiumConversionReward
      ? t('settings.earnCredits.referralNote', {
          referral: referrerReward.credits,
          premium: premiumConversionReward.credits,
        })
      : null;

  const earnedSubtitle = referrerReward
    ? t('settings.shareApp.earnedSubtitle', {
        credits: referrerReward.credits,
        agents: referrerReward.subAgentRuns,
        briefs: referrerReward.agentMeetings,
      })
    : t('settings.shareApp.earnedSubtitleNoAmount');

  if (variant === 'mobile') {
    return (
      <ShareAppModalMobile
        open={open}
        close={close}
        share={share}
        steps={steps}
        joined={joined}
        earnedTitle={earnedTitle}
        earnedSubtitle={earnedSubtitle}
        referralCredits={referrerReward?.credits ?? null}
        referralNote={referralNote}
        showReferrals={showReferrals}
        referrals={referrals}
        shareLink={shareLink}
        loading={loading}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="medium"
      maxHeightPx={650}
      labelledBy="share-app-title"
      title={t('settings.shareApp.heading')}
      description={t('settings.shareApp.subtitle')}
      headerSeparator
      footerSeparator
      // The "Earn credits" block makes the body outgrow the 650 ceiling, so the overflow moves INTO the
      // body region — header, footer and the invite link stay pinned where the frame puts them.
      scrollBody
      // The invite link is the modal's ACTION, so it belongs in the footer behind the DS separator, where
      // the Figma puts it (header 103 + content 424 + footer 123 = the 650 ceiling above). It used to sit
      // at the bottom of the left column, which made it read as a third step rather than as the thing the
      // modal is for — and it moved every time the referrals list changed height.
      footer={
        <div className={styles.footerSlot}>
          <InviteLinkRow shareLink={shareLink} loading={loading} size="medium" />
          <Button variant="outlined" onClick={close}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      <div className={`${styles.body} ${showReferrals ? styles.twoCol : ''}`}>
        <div className={styles.left}>
          {/* The profile link leads: it is the owner's referral code too (profile links, entry 2). */}
          <ProfileLinkShareSection />
          <div className={styles.steps}>
            {steps.map((s, i) => (
              <div key={i} className={styles.step}>
                <span aria-hidden className={styles.stepNum}>
                  {i + 1}
                </span>
                <span className={styles.stepText}>
                  <span className={styles.stepTitle}>{s.title}</span>
                  <span className={styles.stepBody}>{s.body}</span>
                </span>
              </div>
            ))}
          </div>
          <div className={styles.earned}>
            <span className={styles.earnedTitle}>{earnedTitle}</span>
            <span className={styles.earnedSubtitle}>{earnedSubtitle}</span>
          </div>
          {/* The last line of PROGRAM 1 (invite a friend). Everything above it in this column plus the
              referrals column to its side is the referral program; the rule drawn by `.earnCredits`
              below closes it and opens PROGRAM 2 (social posts). */}
          {referralNote && <p className={styles.referralNote}>{referralNote}</p>}
          <EarnCreditsSection open={open} className={styles.earnCredits} />
        </div>

        {showReferrals && (
          <div className={styles.referrals}>
            <div className={styles.referralHead}>
              <span className={styles.referralTitle}>{t('settings.shareApp.successfulReferrals')}</span>
              <span className={styles.joined}>{t('settings.shareApp.joined', { count: joined })}</span>
            </div>
            <ScrollArea className={styles.referralList}>
              {/* ANONYMOUS. The server no longer sends the referee's name or email — naming the people
                  who joined through a publicly-posted link hands one user another user's identity for a
                  reward they can read from a count, and it disagreed with the deliberately-anonymous
                  "someone joined with your link" push. The avatar keeps its slot so the row still reads
                  as a person, seeded by the referral id rather than a name. */}
              {referrals.map((r) => (
                <div key={r.id} className={styles.referralRow}>
                  <ReferralAvatar name={r.id} size={40} anonymous />
                  <span className={styles.referralName}>{t('settings.shareApp.someoneJoined')}</span>
                  {/* No badge at all while the amount is unknown — a reward pill with the wrong number
                      on it is worse than none. */}
                  {referrerReward && <Badge variant="agents" label={t('settings.shareApp.reward', { count: referrerReward.credits })} />}
                </div>
              ))}
            </ScrollArea>
          </div>
        )}
      </div>
    </Modal>
  );
}
